import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { ChevronDown, Users, Zap, UtensilsCrossed, Trophy } from 'lucide-react';

const faqs = [
  {
    q: "How many people can Rudragarh accommodate for a group?",
    a: "Rudragarh has a total property capacity of approximately 22 guests across all 6 room types — with multiple units available for most room categories. For groups of up to 10, booking through the website is straightforward. For larger groups or full-property bookings, contact us directly so we can confirm availability across all rooms and help coordinate your stay. We can also discuss group pricing and arrangements when you reach out."
  },
  {
    q: "Are there group activity packages at Rudragarh?",
    a: "Yes — and the property's activity roster is ideal for groups. Rain dance on the floor is a group favourite. Archery tournaments, ATV rides on the dunes, football, air gun shooting, and camel safari all work brilliantly as group activities. Open-air theatre and cultural programs provide a shared evening experience. When booking as a group, let us know the size and composition of your party and we can help structure activity sessions around your schedule."
  },
  {
    q: "How do I book multiple rooms for a group?",
    a: "You can book each room type separately via the website — most room categories have multiple units available. Alternatively, for groups of 6 or more, contact us directly and we'll help coordinate availability across multiple rooms, confirm we can accommodate your full party on the same dates, and ensure your group experience at Rudragarh is properly set up before arrival."
  }
];

const highlights = [
  {
    icon: <Users size={28} />,
    title: "Up to 22 Guests",
    desc: "With 6 room types and multiple units across the property, Rudragarh comfortably accommodates groups — from a group of friends to a full company retreat."
  },
  {
    icon: <Trophy size={28} />,
    title: "Group Activities Built In",
    desc: "Archery tournaments, rain dance floor, ATV rides, football, camel safari — activities that work better with a group and need no external booking."
  },
  {
    icon: <UtensilsCrossed size={28} />,
    title: "Grand Dining Hall",
    desc: "A full-capacity dining hall means the whole group eats together. The live kitchen and grand dining hall make every meal a shared occasion."
  },
  {
    icon: <Zap size={28} />,
    title: "Open-Air Theatre",
    desc: "Cultural programs and open-air theatre under the desert sky make for a group evening that no bar or nightclub can replicate."
  }
];

const rooms = [
  {
    slug: "heritage-huts",
    name: "The Heritage Huts",
    price: 15000,
    guests: 4,
    category: "unique",
    desc: "Four units available — book them all for a group of up to 16 in the bamboo huts alone. Two levels, two beds, earthy and adventurous."
  },
  {
    slug: "cascade-cottages",
    name: "Cascade Cottages",
    price: 12000,
    guests: 2,
    category: "heritage",
    desc: "Four standalone units with heritage Rajasthani architecture. Comfortable, charming, and available to book as a cluster for groups."
  },
  {
    slug: "cocoon",
    name: "The Cocoon",
    price: 4200,
    guests: 2,
    category: "heritage",
    desc: "Four artisan wooden rooms — our most affordable option, bookable in multiples for groups who want their own private spaces."
  }
];

const Groups = () => {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="min-h-screen pt-20">
      <Helmet>
        <title>Group Trip to Jaisalmer | Rudragarh — Desert Resort for Groups</title>
        <meta name="description" content="Plan your group trip to Jaisalmer at Rudragarh Resort — multiple rooms available, group activities (ATV, archery, rain dance, camel safari), dining hall, and private desert experiences." />
        <meta property="og:title" content="Group Trip to Jaisalmer | Rudragarh — Desert Resort for Groups" />
        <meta property="og:description" content="Plan your group trip to Jaisalmer at Rudragarh Resort — multiple rooms available, group activities (ATV, archery, rain dance, camel safari), dining hall, and private desert experiences." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.rudragarh.com/for/groups" />
        <meta property="og:image" content="https://www.rudragarh.com/images/resort-lawn.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Group Trip to Jaisalmer | Rudragarh Resort" />
        <meta name="twitter:description" content="Multiple rooms, group activities, grand dining hall — Rudragarh is built for group trips to Jaisalmer." />
        <meta name="twitter:image" content="https://www.rudragarh.com/images/resort-lawn.jpg" />
        <link rel="canonical" href="https://www.rudragarh.com/for/groups" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqs.map(f => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a }
          }))
        })}</script>
      </Helmet>

      {/* Hero */}
      <section className="relative h-[55vh] flex items-center justify-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('/images/resort-lawn.jpg')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-void/70 via-void/50 to-void" />
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <span className="overline">Jaisalmer, Rajasthan</span>
            <h1 className="text-4xl md:text-6xl font-heading font-medium mb-4">
              Group Trips to <span className="text-gold-500">Jaisalmer</span>
            </h1>
            <p className="text-foreground/70 max-w-2xl mx-auto text-lg">
              Multiple rooms, a grand dining hall, ATV rides, archery, rain dance, and a private desert that belongs to your group — Rudragarh is made for this.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Intro */}
      <section className="section-padding bg-void-paper">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <span className="overline">For Groups of Friends, Colleagues & Crews</span>
            <h2 className="text-3xl md:text-4xl font-heading font-medium">
              The group trip that <span className="text-gold-500">actually delivers.</span>
            </h2>
            <p className="text-foreground/70 leading-relaxed">
              Group trips to Jaisalmer usually go one of two ways: a generic resort that accommodates you but doesn't particularly feel like an experience, or a camp setup that's fun but light on comfort. Rudragarh sits in a different category — a boutique property with enough rooms to take a group, enough activities to fill three days, and enough character to make the whole thing feel like a genuine destination rather than just a location.
            </p>
            <p className="text-foreground/70 leading-relaxed">
              Bachelor and bachelorette parties, college friend reunions, team off-sites, birthday groups — Rudragarh works for all of them. The activity list is built for group energy: rain dance on the floor, archery tournaments, ATV rides on private dunes, football, camel safari, and open-air theatre in the evenings. The grand dining hall means the whole group eats together. The private property means you're not sharing any of it with strangers.
            </p>
            <p className="text-foreground/70 leading-relaxed">
              For groups of 6 or more, we recommend reaching out directly — we can coordinate multi-room availability, discuss arrangements, and make sure everything lines up before you arrive. The booking team is responsive and knows the property well.
            </p>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="text-center mb-14">
            <span className="overline">Why Groups Choose Rudragarh</span>
            <h2 className="text-3xl md:text-4xl font-heading font-medium">
              Built for <span className="text-gold-500">Group Energy</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {highlights.map((h, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-void-subtle border border-gold-500/10 p-8 space-y-4"
              >
                <div className="text-gold-500">{h.icon}</div>
                <h3 className="font-heading text-xl">{h.title}</h3>
                <p className="text-foreground/60 text-sm leading-relaxed">{h.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Rooms */}
      <section className="section-padding bg-void-paper">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="text-center mb-14">
            <span className="overline">Multi-Unit Availability</span>
            <h2 className="text-3xl md:text-4xl font-heading font-medium">
              Rooms for the <span className="text-gold-500">Whole Group</span>
            </h2>
            <p className="text-foreground/60 mt-4 max-w-xl mx-auto">
              Most room categories have 4 units available — book in multiples for your group or mix and match across room types.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {rooms.map((room, i) => (
              <motion.div
                key={room.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-void-subtle border border-gold-500/10 group"
              >
                <Link to={`/stay/${room.slug}`}>
                  <div className="p-6 space-y-3">
                    <span className="text-gold-500 text-xs uppercase tracking-widest">{room.category}</span>
                    <h3 className="font-heading text-xl group-hover:text-gold-500 transition-colors">{room.name}</h3>
                    <p className="text-foreground/60 text-sm leading-relaxed">{room.desc}</p>
                    <div className="flex items-center justify-between pt-4 border-t border-gold-500/10">
                      <span className="text-gold-500 font-heading text-lg">₹{room.price.toLocaleString()}<span className="text-foreground/40 text-sm font-normal">/night</span></span>
                      <span className="text-foreground/40 text-sm">Up to {room.guests} guests</span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/stay" className="btn-outline">View All Rooms</Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding">
        <div className="max-w-3xl mx-auto container-padding">
          <div className="text-center mb-12">
            <span className="overline">Common Questions</span>
            <h2 className="text-3xl md:text-4xl font-heading font-medium">
              Group Bookings at <span className="text-gold-500">Rudragarh</span>
            </h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-void-subtle border border-gold-500/10">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-6 text-left"
                >
                  <span className="font-heading text-lg pr-4">{faq.q}</span>
                  <ChevronDown
                    size={20}
                    className={`text-gold-500 flex-shrink-0 transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`}
                  />
                </button>
                {openFaq === i && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="px-6 pb-6"
                  >
                    <p className="text-foreground/70 leading-relaxed">{faq.a}</p>
                  </motion.div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-32">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('/images/resort-lawn.jpg')` }}
        />
        <div className="absolute inset-0 bg-void/80" />
        <div className="relative z-10 max-w-3xl mx-auto text-center px-6">
          <span className="overline">Bring the Group</span>
          <h2 className="text-3xl md:text-5xl font-heading font-medium mb-6">
            The Desert Is Better Together
          </h2>
          <p className="text-foreground/70 mb-10 max-w-xl mx-auto">
            Book online for smaller groups, or contact us directly for larger parties — we'll help coordinate rooms, activities, and arrivals so the trip runs smoothly.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/book" className="bg-gold-500 text-black px-8 py-4 uppercase tracking-widest text-sm hover:bg-gold-400 transition-colors inline-block">
              Book Your Rooms
            </Link>
            <Link to="/contact" className="btn-outline">
              Contact Us for Groups
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Groups;
